import React, { useEffect } from 'react';
import cx from 'classnames';
import { Form, FormSpy } from 'react-final-form';
import { useSelector, useDispatch } from 'react-redux';
// zola-ui
import { Accordion } from '@zola/zola-ui/src/components/Accordion';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import FinalFormInput from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
import {
  REQUIRED,
  MAX_LENGTH_50,
  MAX_LENGTH_40,
} from '@zola/zola-ui/src/components/Form/util/validations';
import { getCookie, setCookie } from '@zola-helpers/client/dist/es/util/storage';
// actions
import { submitWeddingAndWebsiteDate } from 'actions/WeddingActions';
import { updatePreview } from 'actions/website/WebsitePreviewActions';
// selectors
import { getWeddingAccount } from 'selectors/website/websiteSelectors';
import { getWedding } from 'selectors/wedding';
import { getRegistryIsPublic } from 'selectors/registry/registrySelectors';
// components
import FieldSetWebsiteWeddingDate from '../FieldSetWebsiteWeddingDate';
import { WeddingDateDisplayFormatEnum } from '../../../../../../../util/websiteDateUtil';

import {
  mapDataToFormValues,
  FormValues,
  mapFormValuesToUpdateWeddingRequest,
  mapFormValuesToUpdateWebsiteDateRequest,
  mapFormValuesToWebsitePreview,
} from './mappers';
import styles from './WeddingDetailsAccordion.module.less';

const WeddingDetailsAccordion = (): JSX.Element => {
  const dispatch = useDispatch();
  const wedding = useSelector(getWedding);
  const account = useSelector(getWeddingAccount);
  const registryIsPublic = useSelector(getRegistryIsPublic);
  const EDIT_PAGES_HOME_WEDDING_DETAILS_FORM_COOKIE_KEY = 'editPagesHomeWeddingDetailsForm';
  const firstViewUser = getCookie(EDIT_PAGES_HOME_WEDDING_DETAILS_FORM_COOKIE_KEY) !== 'true';
  useEffect(() => {
    if (firstViewUser) {
      setCookie(EDIT_PAGES_HOME_WEDDING_DETAILS_FORM_COOKIE_KEY, 'true');
    }
  }, []);

  return (
    <Accordion
      items={[
        {
          id: 0,
          hed: 'Wedding Details',
          dek: (
            <Form
              initialValues={mapDataToFormValues({ wedding, account, registryIsPublic })}
              onSubmit={(values: FormValues) => {
                const weddingRequest = mapFormValuesToUpdateWeddingRequest(values);
                const websiteDateRequest = mapFormValuesToUpdateWebsiteDateRequest(values);
                if (weddingRequest) {
                  dispatch(submitWeddingAndWebsiteDate(weddingRequest, websiteDateRequest));
                }
              }}
            >
              {({ handleSubmit, pristine, submitting, valid, values, form }) => {
                return (
                  <form onSubmit={handleSubmit} className={styles.form}>
                    <FinalFormInput
                      name="wedding.title"
                      id="wedding-info-title"
                      label="Website title"
                      maxChars={50}
                      isRequired
                      validate={[REQUIRED, MAX_LENGTH_50]}
                    />
                    <FieldSetWebsiteWeddingDate
                      formValues={values}
                      formApi={form}
                      className={cx(styles.form, {
                        [styles.noBottomSpacing]:
                          values.weddingDateDisplayFormat === WeddingDateDisplayFormatEnum.EXACT,
                      })}
                    />
                    <div className={styles.fieldSetLocation}>
                      <FinalFormInput name="wedding.city" id="wedding-city" label="City" />
                      <FinalFormInput
                        name="wedding.state_province"
                        id="wedding-state"
                        label="State, region, and/or country"
                      />
                    </div>
                    <FinalFormInput
                      name="wedding.hashtag"
                      id="wedding-info-hashtag"
                      label="Hashtag"
                      maxChars={40}
                      validate={MAX_LENGTH_40}
                      addOn="hashtag"
                      placeholder="AnythingForLove"
                    />
                    <ButtonV3 disabled={pristine || !valid || submitting} type="submit">
                      Save Details
                    </ButtonV3>
                    <FormSpy
                      subscription={{ values: true, dirty: true }}
                      onChange={({ values: formValues }) => {
                        dispatch(
                          updatePreview(mapFormValuesToWebsitePreview(formValues as FormValues))
                        );
                      }}
                    />
                  </form>
                );
              }}
            </Form>
          ),
        },
      ]}
      arrowOnLeft
      collapsed={!firstViewUser}
    />
  );
};

export default WeddingDetailsAccordion;
